/**
 * Bridges the custom sizing selects (Width / Length / Height / ...) to the product's
 * real variant options, so picking a size updates the price, availability and buy
 * button.
 *
 * The original variant picker stays in the DOM on purpose. variant-dropdown.liquid only
 * hides it (display:none) when the product's custom.custom_sizing metafield is set --
 * it still owns variant matching and it is what block-price listens to. This file just
 * selects the right option on it and lets the picker fire its own change event.
 *
 * Option values are read off the picker at runtime instead of being hardcoded per
 * product, so this works whichever shape a product uses:
 *   per-inch values ("52"), ranges ("48-66"), or named sizes ('Wyoming King 84" x 84"').
 *
 * The line-item properties themselves are collected by block-buy-buttons.js at
 * add-to-cart time; it reads `[name="properties[<sizing id>]"]` inside each container,
 * so that input name must not change.
 */
(function () {
  if (window.__cmfCustomSizingBridge) return
  window.__cmfCustomSizingBridge = true

  var DIMENSION_PATTERN = /(width|length|height|depth|diameter|size)$/

  /**
   * Option names that mean the same dimension. A dimension only ever drives an option
   * whose name is in its own list -- never "the first select" or "the only select",
   * or e.g. Length would end up driving a product whose single option is Width.
   */
  var OPTION_ALIASES = {
    width: ['width', 'size', 'diameter'],
    length: ['length'],
    height: ['height', 'depth'],
    depth: ['depth', 'height'],
    diameter: ['diameter', 'width', 'size'],
    size: ['size', 'width']
  }

  /** Lowercase, drop inch marks, and flatten separators to single spaces. */
  function normalize(text) {
    return String(text == null ? '' : text)
      .toLowerCase()
      .replace(/["'″′]/g, '')
      .replace(/[\s\-_/]+/g, ' ')
      .trim()
  }

  function firstNumber(text) {
    var match = String(text == null ? '' : text).match(/\d+(?:\.\d+)?/)
    return match ? parseFloat(match[0]) : null
  }

  /** What the shopper picked, in every form we may need to compare against. */
  function describeSelection(raw) {
    var normalized = normalize(raw)
    if (!normalized) return null

    return {
      raw: raw,
      normalized: normalized,
      compact: normalized.replace(/ /g, ''),
      number: firstNumber(normalized)
    }
  }

  /** A single <option> on the real variant select. */
  function describeOption(option) {
    var normalized = normalize(option.value)
    // "48-66" / "48 - 66" describe a span of sizes rather than one size.
    var range = normalized.match(/^(\d+(?:\.\d+)?) (\d+(?:\.\d+)?)$/)

    return {
      value: option.value,
      normalized: normalized,
      compact: normalized.replace(/ /g, ''),
      number: firstNumber(normalized),
      min: range ? parseFloat(range[1]) : null,
      max: range ? parseFloat(range[2]) : null
    }
  }

  /**
   * Resolve a selection to one of the select's real option values.
   * Most specific match wins: exact text, then exact number, then a covering range.
   */
  function matchOption(select, selection) {
    var options = Array.prototype.slice
      .call(select.options)
      .filter(function (option) {
        return option.value !== ''
      })
      .map(describeOption)

    var exactText = options.filter(function (option) {
      return option.compact === selection.compact
    })[0]
    if (exactText) return exactText

    if (selection.number === null) return null

    var exactNumber = options.filter(function (option) {
      return option.min === null && option.number === selection.number
    })[0]
    if (exactNumber) return exactNumber

    return (
      options.filter(function (option) {
        return option.min !== null && selection.number >= option.min && selection.number <= option.max
      })[0] || null
    )
  }

  /** 'custom_size_width' -> 'width' */
  function dimensionOf(container) {
    var match = normalize(container.dataset.sizingId).match(DIMENSION_PATTERN)
    return match ? match[1] : null
  }

  /** The variant select that represents this dimension, or null if the product has none. */
  function findTargetSelect(container, dimension) {
    var sectionId = container.dataset.sectionId
    var picker =
      (sectionId && document.querySelector('block-variant-picker[data-section-id="' + sectionId + '"]')) ||
      document.querySelector('block-variant-picker')

    if (!picker) return null

    var selects = Array.prototype.slice.call(picker.querySelectorAll('select[data-variant-input]'))
    if (!selects.length) return null

    function handleOf(select) {
      var wrapper = select.closest('[data-handle]')
      return wrapper ? wrapper.dataset.handle : ''
    }

    function byHandle(handle) {
      return selects.filter(function (select) {
        return handleOf(select) === handle
      })[0]
    }

    // Only ever drive an option that means this dimension. Products whose single sizing
    // option is called "Size" are covered by the alias list, not by falling back to
    // "the only select" -- that would let Length drive a Width-only product.
    var aliases = OPTION_ALIASES[dimension] || [dimension]

    for (var i = 0; i < aliases.length; i++) {
      var match = byHandle(aliases[i])
      if (match) return match
    }

    return null
  }

  /**
   * Store the formatted value for block-buy-buttons.js to read at add-to-cart time.
   * Prefers the real variant option text so the cart shows what the shop uses --
   * except for a range option ("48-66"), which identifies a price band rather than
   * a size. Storing the range there would throw away the only record of what the
   * shopper actually asked for, leaving the supplier with nothing to cut to.
   */
  function storeProperty(container, selection, matched) {
    var sizingId = container.dataset.sizingId
    if (!sizingId) return

    var name = 'properties[' + sizingId + ']'
    var input = container.querySelector('[name="' + name + '"]')

    if (!input) {
      input = document.createElement('input')
      input.type = 'hidden'
      input.name = name
      container.appendChild(input)
    }

    input.value = matched && matched.min === null ? matched.value : selection.raw
  }

  /**
   * Hide the original dropdown for any option a custom sizing select already drives, so
   * the shopper never sees two controls for the same dimension. variant-dropdown.liquid
   * covers Size/Width; this covers whatever else a product exposes. Deliberately scoped
   * to options we actually drive -- an option with no custom sizing block stays visible,
   * otherwise it would become unpickable.
   */
  function hideDrivenOptions() {
    Array.prototype.slice.call(document.querySelectorAll('[data-custom-sizing]')).forEach(function (container) {
      var dimension = dimensionOf(container)
      if (!dimension) return

      var target = findTargetSelect(container, dimension)
      if (!target) return

      var wrapper = target.closest('.variant-wrapper')
      if (wrapper) wrapper.style.display = 'none'
    })
  }

  function handleChange(field) {
    var container = field.closest('[data-custom-sizing]')
    if (!container) return

    var selection = describeSelection(field.value)
    var dimension = dimensionOf(container)
    var sizingId = container.dataset.sizingId

    // Back to the placeholder option: drop the stored property so add-to-cart validation
    // catches it again. The variant is left alone -- there is nothing to fall back to.
    if (!selection && sizingId) {
      var cleared = container.querySelector('[name="properties[' + sizingId + ']"]')
      if (cleared) cleared.value = ''
    }

    var matched = null

    if (selection && dimension) {
      var target = findTargetSelect(container, dimension)

      if (target) {
        matched = matchOption(target, selection)

        if (matched) {
          if (target.value !== matched.value) {
            target.value = matched.value
            // Let the variant picker recalculate; block-price listens for what it emits.
            target.dispatchEvent(new Event('change', { bubbles: true }))
          }
        } else {
          console.warn(
            '[custom-sizing] no ' + dimension + ' option matches "' + field.value + '" on this product'
          )
        }
      }
    }

    if (selection) storeProperty(container, selection, matched)

    // block-buy-buttons.js listens for this to clear its "please select" warning.
    document.dispatchEvent(
      new CustomEvent('customSizeSelected', {
        bubbles: true,
        detail: { dimension: dimension, value: field.value, container: container }
      })
    )
  }

  // Delegated so it keeps working when a section re-renders and replaces these nodes.
  document.addEventListener('change', function (event) {
    var field = event.target.closest('[data-custom-size-select], [data-custom-size-custom]')
    if (field) handleChange(field)
  })

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hideDrivenOptions)
  } else {
    hideDrivenOptions()
  }
})()
